import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";
import { FiAlertTriangle } from "react-icons/fi";
import { useDispatch } from "react-redux";
import PinField from "react-pin-field";

import { verifyUserPin } from "actions/auth";

import useTheme from "hooks/useTheme";

const DeleteByPinModal = ({ isOpen, setIsOpen, title, action, setActive }) => {
	const dispatch = useDispatch();
	const { appLanguage, themeColor } = useTheme();

	const pinRef = useRef(null);

	const [pinInputFilled, setPinInputFilled] = useState(false);
	const [isCorrectPin, setIsCorrectPin] = useState(null);

	useEffect(() => {
		setPinInputFilled(false);
		setIsCorrectPin(null);
	}, [isOpen]);

	const VerifyPin = async (pin) => {
		setPinInputFilled(true);

		await verifyUserPin(pin).then((data) => {
			if (data) {
				setIsCorrectPin("verified");
			} else {
				setIsCorrectPin("invalid");
				setPinInputFilled(false);
				pinRef.current.forEach((input) => (input.value = ""));
				pinRef.current[0].focus();
			}
		});
	};

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog as="div" className="relative z-10" onClose={() => setIsOpen(false)}>
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className={`fixed inset-0 ${themeColor?.bg} bg-opacity-30`} />
				</Transition.Child>

				<div className="fixed inset-0 overflow-y-auto">
					<div className="flex min-h-full items-center justify-center p-4 text-center">
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
								<div className="flex w-full space-x-4 items-center">
									<FiAlertTriangle className="w-fit text-9xl text-candy-red" />

									<div className="flex-col w-full space-y-3">
										<Dialog.Title
											as="h3"
											className="flex-col items-center font-medium leading-6 text-gray-900"
										>
											<p className={`text-lg text-candy-red`}>{appLanguage?.areYouSure}</p>
											<p className={`text-sm text-candy-red`}> {appLanguage?.deleting + " " + title}</p>
										</Dialog.Title>
										<p className={`text-sm text-candy-red`}>{appLanguage?.doYouReallyWantToDeleteThis}</p>
									</div>
								</div>
								<div className="flex-col w-full space-y-2">
									<div
										className={`flex items-center justify-center ${
											isCorrectPin === "invalid" && "animate-[bounce_75ms_linear_2]"
										}`}
									>
										<PinField
											className={`m-1 text-center border border-candy-red outline-none smooth text-5xl pb-2 rounded-full text-candy-red h-10 w-10 focus:border-2 invalid:animate-[bounce_75ms_linear_2] invalid:border-candy-red`}
											onComplete={(e) => VerifyPin(e)}
											onChange={() => setIsCorrectPin(null)}
											ref={pinRef}
											length={4}
											validate="0123456789"
											type="password"
											autoFocus
											disabled={pinInputFilled}
										/>
									</div>
									{isCorrectPin === "invalid" && (
										<p className={`text-center text-sm text-candy-red`}>{appLanguage?.incorrectPin}</p>
									)}
								</div>

								<div className="w-full flex mt-4 space-x-3 justify-end">
									{isCorrectPin && (
										<button
											type="button"
											className={`inline-flex justify-center rounded-md border px-4 py-2 text-sm font-medium text-white bg-candy-red`}
											onClick={() => {
												dispatch(action);
												setActive(null);
											}}
										>
											{appLanguage?.delete}
										</button>
									)}
									<button
										type="button"
										className={`inline-flex justify-center rounded-md border px-4 py-2 text-sm font-medium ${themeColor?.text}`}
										onClick={() => setIsOpen(false)}
									>
										{appLanguage?.cancel}
									</button>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
};

export default DeleteByPinModal;
